/** Dokumentmodell des Editors – spiegelt backend/src/domain/document.ts. */

export type Alignment = 'left' | 'center' | 'right' | 'justify';
export type BorderLineStyle = 'solid' | 'dashed' | 'dotted';
export interface BorderSpec { width?: number; color?: string; style?: BorderLineStyle }
export interface Borders { top?: BorderSpec; right?: BorderSpec; bottom?: BorderSpec; left?: BorderSpec }
export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

export interface DesignerStyle {
  styleRef?: string;
  align?: Alignment;
  fontFamily?: string; fontSize?: number;
  bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean;
  color?: string; background?: string;
  lineHeight?: number;
  marginTop?: number; marginBottom?: number; padding?: number;
  indentLeft?: number; indentRight?: number; firstLineIndent?: number;
  letterSpacing?: number; textTransform?: TextTransform;
  keepWithNext?: boolean; keepTogether?: boolean;
  pageBreakBefore?: boolean; pageBreakAfter?: boolean;
  borders?: Borders; borderRadius?: number;
  columnGap?: number; verticalAlign?: 'top' | 'middle' | 'bottom';
}

export interface TextMark {
  from: number; to: number;
  bold?: boolean; italic?: boolean; underline?: boolean; strike?: boolean;
  superscript?: boolean; subscript?: boolean; code?: boolean; smallCaps?: boolean;
  color?: string; background?: string; link?: string;
  fontFamily?: string; fontSize?: number; letterSpacing?: number;
}
export type MarkFlags = Omit<TextMark, 'from' | 'to'>;
export const BOOLEAN_MARK_KEYS = ['bold', 'italic', 'underline', 'strike', 'superscript', 'subscript', 'code', 'smallCaps'] as const;
export const VALUE_MARK_KEYS = ['color', 'background', 'link', 'fontFamily', 'fontSize', 'letterSpacing'] as const;
export const MARK_KEYS = [...BOOLEAN_MARK_KEYS, ...VALUE_MARK_KEYS] as const;
export type BooleanMarkKey = typeof BOOLEAN_MARK_KEYS[number];
export type MarkKey = typeof MARK_KEYS[number];

export type ListMarker = 'disc' | 'circle' | 'square' | 'dash' | 'decimal' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman';
export interface ListItem { id: string; text: string; marks?: TextMark[]; level?: number; style?: DesignerStyle }

/** Eine unterschreibende Vertragspartei im Unterschriftenblock. */
export interface SignatureParty { id: string; name: string }
/** Obergrenze der Vertragsparteien eines Unterschriftenblocks – identisch zur Serverprüfung. */
export const MAX_SIGNATURE_PARTIES = 40;

export type DesignerNodeType = 'paragraph' | 'heading' | 'list' | 'container' | 'pageBreak' | 'divider' | 'spacer' | 'image' | 'toc' | 'qr' | 'signature' | 'signatureBlock' | 'table';
export const NODE_TYPES: DesignerNodeType[] = ['paragraph', 'heading', 'list', 'container', 'pageBreak', 'divider', 'spacer', 'image', 'toc', 'qr', 'signature', 'signatureBlock', 'table'];

/** Bewusst „weiter“ Typ: bestehende Seiten greifen generisch auf Felder zu. */
export interface DesignerNode {
  id: string; type: DesignerNodeType;
  text?: string; marks?: TextMark[]; level?: 1 | 2 | 3 | 4 | 5 | 6; numbered?: boolean;
  ordered?: boolean; marker?: ListMarker; start?: number; items?: ListItem[];
  columns?: any[]; rows?: any[]; headerRow?: boolean; zebra?: boolean; borderColor?: string;
  payload?: any; caption?: string; size?: number;
  label?: string; role?: string; provider?: string; showDate?: boolean; showPlace?: boolean;
  parties?: SignatureParty[]; perRow?: number; signHeight?: number;
  src?: string; alt?: string; width?: number; height?: number;
  title?: string; maxLevel?: number;
  style?: DesignerStyle;
}

export type PageSize = 'A3' | 'A4' | 'A5' | 'LETTER' | 'LEGAL';
export const PAGE_SIZES: Record<PageSize, { width: number; height: number; label: string }> = {
  A3: { width: 297, height: 420, label: 'A3' },
  A4: { width: 210, height: 297, label: 'A4' },
  A5: { width: 148, height: 210, label: 'A5' },
  LETTER: { width: 215.9, height: 279.4, label: 'Letter' },
  LEGAL: { width: 215.9, height: 355.6, label: 'Legal' },
};
export interface PageSetup {
  size: PageSize; orientation: 'portrait' | 'landscape';
  marginTop: number; marginRight: number; marginBottom: number; marginLeft: number;
  columns?: number; columnGap?: number;
}
export interface ParagraphStyleDef { id: string; name: string; appliesTo?: 'paragraph' | 'heading' | 'list' | 'any'; style: DesignerStyle }
export interface DocumentSettings { headingNumbering?: 'none' | 'decimal' | 'paragraph'; numberingPrefix?: string; tabSize?: number }
export interface DesignerDocument {
  schemaVersion: 1; nodes: DesignerNode[];
  page?: PageSetup; styles?: ParagraphStyleDef[]; defaults?: DesignerStyle; settings?: DocumentSettings;
}

export const DEFAULT_PAGE: PageSetup = { size: 'A4', orientation: 'portrait', marginTop: 20, marginRight: 20, marginBottom: 20, marginLeft: 25, columns: 1, columnGap: 8 };
export const DEFAULT_TEXT_STYLE: DesignerStyle = { fontFamily: 'Helvetica', fontSize: 10, lineHeight: 1.4, color: '#111827', align: 'left', marginBottom: 6 };
export const HEADING_SIZES: Record<number, number> = { 1: 22, 2: 16, 3: 13, 4: 12, 5: 11, 6: 10 };

/** PDF-taugliche Schriftfamilien (react-pdf Standardschriften). */
export const FONT_FAMILIES = [
  { value: 'Helvetica', label: 'Helvetica (serifenlos)' },
  { value: 'Times-Roman', label: 'Times (Serif)' },
  { value: 'Courier', label: 'Courier (dicktengleich)' },
];
export const FONT_SIZES = [6, 7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];
export const LINE_HEIGHTS = [1, 1.15, 1.3, 1.4, 1.5, 1.75, 2, 2.5, 3];
export const SWATCHES = ['#111827', '#374151', '#6b7280', '#9ca3af', '#ffffff', '#b91c1c', '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a', '#059669', '#0d9488', '#0891b2', '#0284c7', '#2563eb', '#4f46e5', '#7c3aed', '#c026d3', '#db2777'];
export const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#e9d5ff', '#e5e7eb'];

/** crypto.randomUUID gibt es nur in sicheren Kontexten (nicht über http://<ip>). */
export const uid = (): string => {
  const c: Crypto | undefined = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  if (c?.getRandomValues) {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};

export const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export function pageGeometry(page: PageSetup = DEFAULT_PAGE) {
  const base = PAGE_SIZES[page.size] || PAGE_SIZES.A4;
  const widthMm = page.orientation === 'landscape' ? base.height : base.width;
  const heightMm = page.orientation === 'landscape' ? base.width : base.height;
  return {
    widthMm, heightMm,
    contentWidthMm: Math.max(10, widthMm - page.marginLeft - page.marginRight),
    contentHeightMm: Math.max(10, heightMm - page.marginTop - page.marginBottom),
  };
}
export const PX_PER_MM = 96 / 25.4;

export function resolveStyle(document: Pick<DesignerDocument, 'styles' | 'defaults'>, style?: DesignerStyle): DesignerStyle {
  const named = style?.styleRef ? document.styles?.find(entry => entry.id === style.styleRef)?.style : undefined;
  return { ...DEFAULT_TEXT_STYLE, ...(document.defaults || {}), ...(named || {}), ...(style || {}) };
}

// ==================== Mark-Algebra ====================

const flagsOf = (mark: TextMark): MarkFlags => {
  const flags: MarkFlags = {};
  for (const key of MARK_KEYS) {
    const value = (mark as any)[key];
    if (value !== undefined && value !== null && value !== false && value !== '') (flags as any)[key] = value;
  }
  return flags;
};
const flagKey = (flags: MarkFlags) => MARK_KEYS.map(key => `${key}=${(flags as any)[key] ?? ''}`).join('|');
const hasFlags = (flags: MarkFlags) => MARK_KEYS.some(key => (flags as any)[key] !== undefined);

/** Zeichenweise Attributtabelle – Grundlage aller Formatierungsoperationen. */
function toCharFlags(text: string, marks: TextMark[] = []): MarkFlags[] {
  const chars: MarkFlags[] = new Array(text.length);
  for (let index = 0; index < text.length; index++) chars[index] = {};
  for (const mark of marks) {
    const flags = flagsOf(mark);
    const from = Math.max(0, Math.floor(mark.from));
    const to = Math.min(text.length, Math.ceil(mark.to));
    for (let index = from; index < to; index++) chars[index] = { ...chars[index], ...flags };
  }
  return chars;
}

function fromCharFlags(chars: MarkFlags[]): TextMark[] {
  const marks: TextMark[] = [];
  let start = 0;
  while (start < chars.length) {
    const key = flagKey(chars[start]);
    let end = start + 1;
    while (end < chars.length && flagKey(chars[end]) === key) end++;
    if (hasFlags(chars[start])) marks.push({ from: start, to: end, ...chars[start] });
    start = end;
  }
  return marks;
}

export const markHasFlags = (flags: MarkFlags) => hasFlags(flags);
/** Baut aus einer zeichenweisen Attributtabelle wieder kompakte Marks. */
export const charFlagsToMarks = (chars: MarkFlags[]): TextMark[] => fromCharFlags(chars);

export interface MarkRun { from: number; to: number; flags: MarkFlags }
/** Lückenlose Läufe gleicher Formatierung – für Anzeige und HTML-Serialisierung. */
export function markRuns(text: string, marks: TextMark[] = []): MarkRun[] {
  if (!text.length) return [];
  const chars = toCharFlags(text, marks);
  const runs: MarkRun[] = [];
  let start = 0;
  while (start < chars.length) {
    const key = flagKey(chars[start]);
    let end = start + 1;
    while (end < chars.length && flagKey(chars[end]) === key) end++;
    runs.push({ from: start, to: end, flags: chars[start] });
    start = end;
  }
  return runs;
}

export function normalizeMarks(text: string, marks: TextMark[] = []): TextMark[] {
  return fromCharFlags(toCharFlags(text, marks));
}

/** Formatierungen, die im gesamten Bereich gelten (Schalter = nur wenn lückenlos aktiv). */
export function activeMarks(text: string, marks: TextMark[] = [], from: number, to: number): MarkFlags {
  const chars = toCharFlags(text, marks).slice(Math.max(0, from), Math.max(0, to));
  if (!chars.length) return {};
  const result: MarkFlags = {};
  for (const key of MARK_KEYS) {
    const first = (chars[0] as any)[key];
    if (first === undefined) continue;
    if (chars.every(entry => (entry as any)[key] === first)) (result as any)[key] = first;
  }
  return result;
}

/** Setzt bzw. entfernt (Wert `undefined`) Formatierungen im Bereich. */
export function applyMarks(text: string, marks: TextMark[] = [], from: number, to: number, patch: Partial<MarkFlags>): TextMark[] {
  const chars = toCharFlags(text, marks);
  const start = Math.max(0, Math.min(text.length, from));
  const end = Math.max(start, Math.min(text.length, to));
  for (let index = start; index < end; index++) {
    const next = { ...chars[index] };
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === null || value === false || value === '') delete (next as any)[key];
      else (next as any)[key] = value;
    }
    chars[index] = next;
  }
  return fromCharFlags(chars);
}

export function toggleMark(text: string, marks: TextMark[] = [], from: number, to: number, key: BooleanMarkKey): TextMark[] {
  const active = !!(activeMarks(text, marks, from, to) as any)[key];
  const patch: Partial<MarkFlags> = { [key]: !active } as any;
  if (key === 'superscript' && !active) patch.subscript = undefined;
  if (key === 'subscript' && !active) patch.superscript = undefined;
  return applyMarks(text, marks, from, to, patch);
}

export function clearMarks(text: string, marks: TextMark[] = [], from: number, to: number): TextMark[] {
  const chars = toCharFlags(text, marks);
  for (let index = Math.max(0, from); index < Math.min(text.length, to); index++) chars[index] = {};
  return fromCharFlags(chars);
}

/** Formatierungen eines Textausschnitts, auf 0 verschoben (z. B. beim Absatzteilen). */
export function sliceMarks(text: string, marks: TextMark[] = [], from: number, to: number): TextMark[] {
  const chars = toCharFlags(text, marks).slice(from, to);
  return fromCharFlags(chars);
}

/** Fügt Text an einer Position ein und behält die Formatierung des Umfelds. */
export function insertText(text: string, marks: TextMark[] = [], at: number, insertion: string, flags: MarkFlags = {}): { text: string; marks: TextMark[] } {
  const chars = toCharFlags(text, marks);
  const inserted = Array.from({ length: insertion.length }, () => ({ ...flags }));
  const next = [...chars.slice(0, at), ...inserted, ...chars.slice(at)];
  return { text: text.slice(0, at) + insertion + text.slice(at), marks: fromCharFlags(next) };
}

export function removeRange(text: string, marks: TextMark[] = [], from: number, to: number): { text: string; marks: TextMark[] } {
  const chars = toCharFlags(text, marks);
  const next = [...chars.slice(0, from), ...chars.slice(to)];
  return { text: text.slice(0, from) + text.slice(to), marks: fromCharFlags(next) };
}

// ==================== Dokument-Hilfen ====================

export const isTextNode = (node?: DesignerNode) => node?.type === 'paragraph' || node?.type === 'heading';

export function nodeText(node: DesignerNode): string {
  if (isTextNode(node)) return node.text || '';
  if (node.type === 'list') return (node.items || []).map(item => item.text || '').join('\n');
  if (node.type === 'container') return (node.columns || []).map((column: any) => (column.nodes || []).map(nodeText).join('\n')).join('\n');
  if (node.type === 'table') return (node.rows || []).map((row: any) => (node.columns || []).map((column: any) => String(row.cells?.[column.key] ?? '')).join('\t')).join('\n');
  if (node.type === 'signature') return node.label || '';
  if (node.type === 'signatureBlock') return (node.parties || []).map(party => party.name || '').filter(Boolean).join('\n');
  if (node.type === 'image') return node.caption || '';
  return '';
}

export function documentToPlainText(document: DesignerDocument): string {
  return document.nodes.map(nodeText).filter(Boolean).join('\n\n');
}

export function documentStatistics(document: DesignerDocument) {
  const text = documentToPlainText(document);
  const words = text.split(/\s+/).filter(Boolean).length;
  let paragraphs = 0, headings = 0, tables = 0, images = 0, signatures = 0;
  const walk = (nodes: DesignerNode[]) => nodes.forEach(node => {
    if (node.type === 'paragraph') paragraphs++;
    if (node.type === 'heading') headings++;
    if (node.type === 'table') tables++;
    if (node.type === 'image') images++;
    if (node.type === 'signature') signatures++;
    if (node.type === 'signatureBlock') signatures += (node.parties || []).length;
    if (node.type === 'container') (node.columns || []).forEach((column: any) => walk(column.nodes || []));
  });
  walk(document.nodes);
  return { words, characters: text.length, paragraphs, headings, tables, images, signatures, nodes: document.nodes.length };
}

/** Nummerierung der Überschriften (1.1 bzw. §1) – identisch zur PDF-Ausgabe. */
export function headingNumbers(document: DesignerDocument): Map<string, string> {
  const mode = document.settings?.headingNumbering || 'none';
  const numbers = new Map<string, string>();
  if (mode === 'none') return numbers;
  const counters: number[] = [];
  const walk = (nodes: DesignerNode[]) => {
    for (const node of nodes) {
      if (node.type === 'container') { (node.columns || []).forEach((column: any) => walk(column.nodes || [])); continue; }
      if (node.type !== 'heading' || node.numbered === false) continue;
      const level = Math.max(1, Math.min(6, node.level || 1));
      counters.length = level;
      counters[level - 1] = (counters[level - 1] || 0) + 1;
      for (let index = 0; index < level; index++) counters[index] = counters[index] || 1;
      numbers.set(node.id, mode === 'paragraph' && level === 1
        ? `${document.settings?.numberingPrefix ?? '§'}${counters[0]}`
        : counters.slice(0, level).join('.'));
    }
  };
  walk(document.nodes);
  return numbers;
}

/** Aufzählungszeichen eines Listeneintrags anhand Ebene und Position. */
export function listMarkerLabel(node: DesignerNode, index: number): string {
  const items = node.items || [];
  const level = items[index]?.level || 0;
  if (!node.ordered) {
    const cycle: ListMarker[] = ['disc', 'circle', 'square', 'dash'];
    const marker = node.marker && ['disc', 'circle', 'square', 'dash'].includes(node.marker) ? node.marker : cycle[level % cycle.length];
    return { disc: '•', circle: '◦', square: '▪', dash: '–' }[marker as 'disc'] || '•';
  }
  let counter = node.start && node.start > 0 ? node.start : 1;
  for (let position = 0; position < index; position++) {
    const itemLevel = items[position]?.level || 0;
    if (itemLevel === level) counter++;
    if (itemLevel < level) counter = node.start && node.start > 0 ? node.start : 1;
  }
  const ordered: ListMarker[] = ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'];
  const cycle: ListMarker[] = ['decimal', 'lower-alpha', 'lower-roman', 'upper-alpha'];
  const marker = node.marker && ordered.includes(node.marker) ? node.marker : cycle[level % cycle.length];
  if (marker === 'lower-alpha') return `${alphabetic(counter).toLowerCase()}.`;
  if (marker === 'upper-alpha') return `${alphabetic(counter)}.`;
  if (marker === 'lower-roman') return `${roman(counter).toLowerCase()}.`;
  if (marker === 'upper-roman') return `${roman(counter)}.`;
  return `${counter}.`;
}

function alphabetic(value: number): string {
  let result = '', remaining = value;
  while (remaining > 0) { const rest = (remaining - 1) % 26; result = String.fromCharCode(65 + rest) + result; remaining = Math.floor((remaining - 1) / 26); }
  return result || 'A';
}
function roman(value: number): string {
  const table: [number, string][] = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let remaining = value, result = '';
  for (const [amount, symbol] of table) while (remaining >= amount) { result += symbol; remaining -= amount; }
  return result || 'I';
}

/** Ersetzt Knoten-IDs rekursiv – beim Einfügen von Bausteinen und Duplizieren. */
export function renewIds(node: DesignerNode): DesignerNode {
  const copy = clone(node);
  copy.id = uid();
  if (copy.items) copy.items = copy.items.map(item => ({ ...item, id: uid() }));
  if (copy.parties) copy.parties = copy.parties.map(party => ({ ...party, id: uid() }));
  if (copy.type === 'container' && copy.columns) {
    copy.columns = copy.columns.map((column: any) => ({ ...column, id: uid(), nodes: (column.nodes || []).map(renewIds) }));
  } else if (copy.type === 'table') {
    if (copy.columns) copy.columns = copy.columns.map((column: any) => ({ ...column, id: uid() }));
    if (copy.rows) copy.rows = copy.rows.map((row: any) => ({ ...row, id: uid() }));
  }
  return copy;
}

export const emptyDocument = (): DesignerDocument => ({ schemaVersion: 1, nodes: [], page: { ...DEFAULT_PAGE }, styles: [], settings: { headingNumbering: 'none' } });

/** Nutzt ausschließlich in react-pdf verfügbare Standardschriften. */
export function pdfFontName(family: string | undefined, bold?: boolean, italic?: boolean): string {
  const base = family === 'Times-Roman' || family === 'Times' ? 'Times' : family === 'Courier' ? 'Courier' : 'Helvetica';
  if (base === 'Times') return bold && italic ? 'Times-BoldItalic' : bold ? 'Times-Bold' : italic ? 'Times-Italic' : 'Times-Roman';
  const suffix = bold && italic ? '-BoldOblique' : bold ? '-Bold' : italic ? '-Oblique' : '';
  return `${base}${suffix}`;
}
